import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useAnalytics } from '../../../hooks/useAnalytics';
import { CompactMapView } from '../../analytics/CompactMapView';
import { approximatePolygonAreaM2 } from '../../../utils/areaOverlayUtils';
import '../Workspace/UnifiedModuleBlocks.css';

const toNumber = (value) => {
  if (value == null) {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getStationCoordinates = (station) => {
  if (Array.isArray(station?.coordinates) && station.coordinates.length >= 2) {
    return [station.coordinates[0], station.coordinates[1]];
  }
  if (station?.locationLat != null && station?.locationLon != null) {
    return [station.locationLat, station.locationLon];
  }
  if (station?.lat != null && station?.lon != null) {
    return [station.lat, station.lon];
  }
  if (station?.latitude != null && station?.longitude != null) {
    return [station.latitude, station.longitude];
  }
  return null;
};

const boundsToPolygon = (bounds) => {
  if (!bounds) {
    return null;
  }
  return [
    [bounds.minLat, bounds.minLon],
    [bounds.minLat, bounds.maxLon],
    [bounds.maxLat, bounds.maxLon],
    [bounds.maxLat, bounds.minLon],
  ];
};

const getPolygonBounds = (polygon) => {
  if (!Array.isArray(polygon) || polygon.length === 0) {
    return null;
  }
  const lats = polygon.map(point => point[0]);
  const lons = polygon.map(point => point[1]);
  return {
    minLat: Math.min(...lats),
    maxLat: Math.max(...lats),
    minLon: Math.min(...lons),
    maxLon: Math.max(...lons),
  };
};

const normalizePolygon = (geometry) => {
  if (!geometry) {
    return null;
  }
  if (Array.isArray(geometry)) {
    return geometry;
  }
  if (geometry.type === 'Polygon' && Array.isArray(geometry.coordinates)) {
    const ring = geometry.coordinates[0] || [];
    return ring.map(point => [point[1], point[0]]);
  }
  return null;
};

const resolveZoneId = (zone, index) =>
  zone?.id || zone?.zoneId || zone?.name || zone?.label || `zone-${index + 1}`;

const resolveZoneName = (zone, index) =>
  zone?.name || zone?.areaName || zone?.label || zone?.id || `Zone ${index + 1}`;

const resolveZonePolygon = (zone) => {
  if (!zone) {
    return null;
  }
  const geometryPolygon = normalizePolygon(zone.geometry);
  if (geometryPolygon) {
    return geometryPolygon;
  }
  if (Array.isArray(zone.polygon)) {
    return zone.polygon;
  }
  if (zone.bounds) {
    return boundsToPolygon(zone.bounds);
  }
  return null;
};

const resolveZoneAreaHa = (zone, polygon) => {
  if (!zone) {
    return null;
  }
  const directArea = toNumber(zone.areaHa) ?? toNumber(zone.area);
  if (directArea != null) {
    return directArea;
  }
  if (polygon && polygon.length >= 3) {
    return approximatePolygonAreaM2(polygon) / 10000;
  }
  return null;
};

const getPolygonCenter = (polygon, bounds) => {
  if (Array.isArray(polygon) && polygon.length > 0) {
    const lat = polygon.reduce((sum, point) => sum + point[0], 0) / polygon.length;
    const lon = polygon.reduce((sum, point) => sum + point[1], 0) / polygon.length;
    return [lat, lon];
  }
  if (bounds) {
    return [
      (bounds.minLat + bounds.maxLat) / 2,
      (bounds.minLon + bounds.maxLon) / 2,
    ];
  }
  return null;
};

const normalizePosition = (value) => {
  if (Array.isArray(value) && value.length >= 2) {
    const lat = toNumber(value[0]);
    const lon = toNumber(value[1]);
    if (lat != null && lon != null) {
      return [lat, lon];
    }
  }
  if (value && typeof value === 'object') {
    const lat = toNumber(value.lat ?? value.latitude);
    const lon = toNumber(value.lon ?? value.lng ?? value.longitude);
    if (lat != null && lon != null) {
      return [lat, lon];
    }
  }
  return null;
};

const buildCoverageSections = (coverage) => {
  const zones = Array.isArray(coverage?.zones) ? coverage.zones : [];
  return zones.map((zone, index) => {
    const id = resolveZoneId(zone, index);
    const name = resolveZoneName(zone, index);
    const polygon = resolveZonePolygon(zone);
    const bounds = polygon ? getPolygonBounds(polygon) : zone.bounds || null;
    const center = normalizePosition(zone.position) || getPolygonCenter(polygon, bounds);
    const areaHa = resolveZoneAreaHa(zone, polygon);
    return {
      id: String(id),
      name,
      areaName: name,
      polygon,
      bounds,
      areaHa,
      position: center ? { lat: center[0], lon: center[1] } : undefined,
      stations: [],
    };
  }).filter(section => section.position);
};

const buildCoverageStations = (coverage, zoneLookup) => {
  const stations = Array.isArray(coverage?.stations) ? coverage.stations : [];
  return stations.map((station) => {
    const coords = getStationCoordinates(station);
    if (!coords) {
      return null;
    }
    const zoneId = station?.sectionId || station?.zoneId;
    const zone = zoneId ? zoneLookup.get(String(zoneId)) : null;
    const sensorType = station?.sensorType || station?.stationType || station?.type;
    return {
      ...station,
      locationLat: coords[0],
      locationLon: coords[1],
      sensorType,
      stationType: station?.stationType || station?.sensorType || station?.type,
      sectionId: station?.sectionId || station?.zoneId || station?.areaId || null,
      locationDescription: station?.locationDescription || zone?.name || station?.name || station?.id,
    };
  }).filter(Boolean);
};

const buildCoverageMapData = (coverage) => {
  if (!coverage) {
    return null;
  }
  const sections = buildCoverageSections(coverage);
  if (!sections.length) {
    return null;
  }
  const zoneLookup = new Map(sections.map(section => [String(section.id), section]));
  const stations = buildCoverageStations(coverage, zoneLookup);
  return {
    farm: {
      id: coverage.id || 'coverage-fallback',
      name: coverage.name || 'Coverage',
      sections,
      stations,
    },
    sections,
    stations,
  };
};

const resolveMapData = (selectedFarm, fallbackCoverage, chart) => {
  const shouldPreferFallback = chart?.preferFallbackCoverage === true;
  if (shouldPreferFallback) {
    const fallbackData = buildCoverageMapData(fallbackCoverage);
    if (fallbackData?.sections?.length) {
      return fallbackData;
    }
  }
  if (selectedFarm && Array.isArray(selectedFarm.sections) && selectedFarm.sections.length > 0) {
    return {
      farm: selectedFarm,
      sections: selectedFarm.sections || [],
      stations: selectedFarm.stations || [],
    };
  }
  return buildCoverageMapData(fallbackCoverage);
};

const formatXAxisValue = (value, xAxis = {}) => {
  if (value == null) {
    return '';
  }
  const valueType = xAxis?.valueType;
  const tickFormat = xAxis?.tickFormat || (valueType === 'isoDateTime' ? 'MMM d' : null);
  if ((valueType === 'isoDateTime' || valueType === 'isoDate') && typeof value === 'string' && tickFormat) {
    try {
      return format(parseISO(value), tickFormat);
    } catch (error) {
      return value;
    }
  }
  return String(value);
};

const buildYAxisLabelConfig = (label) => {
  if (!label) {
    return undefined;
  }
  return {
    value: label,
    angle: -90,
    position: 'insideLeft',
    style: { fontSize: 10, fill: '#5f6b64' },
  };
};

export function ReportChartVisual({ chart, emptyLabel, fallbackCoverage, height = 226 }) {
  const { t } = useTranslation();
  const { selectedFarm } = useAnalytics();

  if (chart?.variant === 'map') {
    const mapData = resolveMapData(selectedFarm, fallbackCoverage, chart);
    if (!mapData || !mapData.sections.length) {
      return (
        <div className="unified-module__empty">
          {emptyLabel || t('reports.workspace.blocks.emptyChart')}
        </div>
      );
    }
    const legendVariant = chart?.legendVariant;
    const legendTitle = chart?.legendTitleKey
      ? t(chart.legendTitleKey)
      : chart?.legendTitle;
    const mapAriaLabel = chart?.ariaLabelKey
      ? t(chart.ariaLabelKey)
      : chart?.ariaLabel;
    const showLegend = chart?.showLegend !== false;
    const selectedSections = mapData.sections.map(section => section.id);
    return (
      <div
        className="unified-module__map report-visual-block__map"
        style={Number.isFinite(height) ? { height: `${height}px` } : undefined}
      >
        <CompactMapView
          sections={mapData.sections}
          stations={mapData.stations}
          farm={mapData.farm}
          selectedSections={selectedSections}
          selectAllSections
          interactive={false}
          showSidebar={false}
          showLegend={showLegend}
          legendVariant={legendVariant}
          legendTitle={legendTitle}
          alwaysShowMarkers={chart?.alwaysShowMarkers}
          showMarkerTooltips={chart?.showMarkerTooltips}
          ariaLabel={mapAriaLabel}
        />
      </div>
    );
  }

  const data = Array.isArray(chart?.data) ? chart.data : [];
  const series = Array.isArray(chart?.series)
    ? chart.series.filter(item => item && item.key)
    : [];
  if (data.length === 0) {
    return (
      <div className="unified-module__empty">
        {emptyLabel || t('reports.workspace.blocks.emptyChart')}
      </div>
    );
  }

  const xAxisConfig = chart?.xAxis || null;
  const yAxisConfig = chart?.yAxis || null;
  const shouldShowLegend = series.length > 1 && chart?.showLegend !== false;

  return (
    <div className="unified-module__chart report-visual-block__chart">
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="2 2" stroke="#dfe5e0" />
          <XAxis
            dataKey={chart?.xKey || 'label'}
            tick={{ fontSize: 10, fill: '#5f6b64' }}
            tickFormatter={xAxisConfig ? (value) => formatXAxisValue(value, xAxisConfig) : undefined}
          />
          <YAxis
            tick={{ fontSize: 10, fill: '#5f6b64' }}
            domain={Array.isArray(yAxisConfig?.domain) ? yAxisConfig.domain : undefined}
            label={buildYAxisLabelConfig(yAxisConfig?.label)}
          />
          <Tooltip
            labelFormatter={xAxisConfig ? (value) => formatXAxisValue(value, xAxisConfig) : undefined}
          />
          {shouldShowLegend ? <Legend wrapperStyle={{ fontSize: '11px' }} /> : null}
          {series.length > 0 ? (
            series.map((seriesItem, index) => (
              <Line
                key={seriesItem.key || `series-${index}`}
                type="linear"
                dataKey={seriesItem.key}
                name={seriesItem.label || seriesItem.key}
                stroke={seriesItem.color || '#455642'}
                strokeWidth={Number.isFinite(seriesItem.strokeWidth) ? seriesItem.strokeWidth : 1.8}
                dot={{ r: 1.8 }}
                activeDot={{ r: 3 }}
              />
            ))
          ) : (
            <Line
              type="linear"
              dataKey={chart?.yKey || 'value'}
              stroke="#455642"
              strokeWidth={1.8}
              dot={{ r: 1.8 }}
              activeDot={{ r: 3 }}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export function ReportImageGrid({
  images = [],
  emptyLabel,
  renderImageFooter,
  className = '',
  altFallback = '',
}) {
  const { t } = useTranslation();
  if (!images.length) {
    return (
      <div className="unified-module__empty">
        {emptyLabel || t('reports.workspace.blocks.emptyImages')}
      </div>
    );
  }
  return (
    <div className={`unified-module__image-grid report-visual-block__image-grid ${className}`.trim()}>
      {images.map((image, index) => {
        const altText = image.alt || image.caption || altFallback || '';
        const footerContent = renderImageFooter
          ? renderImageFooter({ image, index })
          : null;
        return (
          <div
            key={`report-image-${index}`}
            className="unified-module__image-card report-visual-block__image-card"
          >
            <img src={image.url} alt={altText} className="unified-module__image" />
            {footerContent ? (
              <div className="unified-module__caption">
                {footerContent}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export function ReportImageCarousel({
  images = [],
  emptyLabel,
  renderFooter,
  className = '',
  altFallback = '',
  interactive = true,
}) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const total = images.length;
  const currentImage = images[index] || images[0];

  useEffect(() => {
    if (index >= images.length && images.length > 0) {
      setIndex(0);
    }
  }, [images.length, index]);

  const handlePrev = () => {
    setIndex(prev => (prev - 1 + total) % total);
  };

  const handleNext = () => {
    setIndex(prev => (prev + 1) % total);
  };

  if (!currentImage) {
    return (
      <div className="unified-module__empty">
        {emptyLabel || t('reports.workspace.blocks.emptyImages')}
      </div>
    );
  }

  const altText = currentImage.alt || currentImage.caption || altFallback || '';
  const footerContent = renderFooter
    ? renderFooter({ image: currentImage, index, total })
    : null;

  return (
    <div className={`unified-module__carousel report-visual-block__carousel ${className}`.trim()}>
      <img src={currentImage.url} alt={altText} className="unified-module__image" />
      {interactive && total > 1 && (
        <div className="unified-module__carousel-controls">
          <button type="button" className="unified-module__carousel-button" onClick={handlePrev}>
            <ChevronLeft size={16} />
          </button>
          <button type="button" className="unified-module__carousel-button" onClick={handleNext}>
            <ChevronRight size={16} />
          </button>
        </div>
      )}
      {total > 1 && (
        <div className="unified-module__carousel-count">
          {index + 1} / {total}
        </div>
      )}
      {footerContent ? (
        <div className="unified-module__caption">
          {footerContent}
        </div>
      ) : null}
    </div>
  );
}
